import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, BadgeCheck, Wallet, Gift } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import LogoMark from "../../components/LogoMark";

export default function CustomerRegister() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState(location.state?.phone || "");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(!!location.state?.phone);
  const [devOtp, setDevOtp] = useState("");
  const [verified, setVerified] = useState(!!location.state?.phone);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, [searchParams]);

  const sendOtp = async () => {
    if (phone.length < 10) return toast.error("Enter valid 10-digit phone");
    setLoading(true);
    try {
      const r = await api.post("/auth/customer/send-otp", { phone });
      setDevOtp(r.data.dev_otp || ""); setOtpSent(true);
      toast.success("OTP sent");
    } catch (_) { toast.error("Failed to send OTP"); }
    setLoading(false);
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) return toast.error("Enter 6-digit OTP");
    setLoading(true);
    try {
      const r = await api.post("/auth/customer/verify-otp", { phone, otp });
      if (!r.data.is_new_user) { login(r.data.token, r.data.user); navigate("/dashboard"); return; }
      setVerified(true); toast.success("Phone verified");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid OTP"); }
    setLoading(false);
  };

  const finish = async () => {
    if (name.trim().length < 2) return toast.error("Enter your full name");
    setLoading(true);
    try {
      const r = await api.post("/auth/customer/register", {
        phone, full_name: name, email,
        referral_code: referralCode || undefined,
      });
      login(r.data.token, r.data.user);
      if (r.data.user.referred_by) toast.success("Welcome! ₹500 referral credit added 🎁");
      else toast.success("Account created!");
      navigate("/kyc");
    } catch (e) { toast.error(e.response?.data?.detail || "Registration failed"); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F4E9]">
      {/* LEFT decorative - sheep image with gradient overlay */}
      <div className="relative hidden lg:block overflow-hidden">
        <img alt="Sheep grazing"
          src="https://images.unsplash.com/photo-1768333005088-4ebfb737faed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxzaGVlcCUyMGZhcm0lMjBncmVlbiUyMGxhbmRzY2FwZXxlbnwwfHx8fDE3NzcxNzg3ODR8MA&ixlib=rb-4.1.0&q=85"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(215,25,33,0.85) 0%, rgba(122,16,16,0.75) 100%)" }} />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white w-fit">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2C97A]/90 text-[#1F1816] text-xs font-bold mb-6">
              ✨ Join 1,200+ investors earning passively
            </div>
            <h2 className="font-display text-4xl xl:text-5xl leading-tight">
              From a single<br />lot to lifelong<br />wealth.
            </h2>
            <ul className="mt-8 space-y-3 text-white/95">
              <Bullet icon={ShieldCheck} text="100% verified KYC and farm operations" />
              <Bullet icon={BadgeCheck} text="Each animal individually tagged with QR" />
              <Bullet icon={Wallet} text="Profits paid out within 7 days of sale" />
            </ul>
          </div>
          <div className="text-xs text-white/60">© 2026 PassiveIearn</div>
        </div>
      </div>

      {/* RIGHT form */}
      <div className="flex flex-col items-center justify-center p-6 md:p-10 relative">
        <Link to="/" className="lg:hidden absolute top-4 left-4 text-sm text-[#6B5F58] flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <LogoMark size="lg" className="mx-auto mb-3" />
            <div className="font-display text-2xl text-[#7A1F1F]">Create your account</div>
            <p className="text-sm text-[#6B5F58] mt-1">Free to start. Earn passive income.</p>
          </div>

          <div className="gf-card p-7 space-y-4 shadow-xl shadow-[#D71921]/10">
            {!verified && (
              <>
                <label className="block">
                  <span className="text-sm font-medium">Mobile number</span>
                  <input data-testid="reg-phone-input" type="tel" maxLength={10}
                    className="mt-2 w-full rounded-xl border border-[#E8E0D2] px-3 py-3 outline-none focus:border-[#D71921] focus:ring-2 focus:ring-[#D71921]/15"
                    placeholder="9876543210" value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} />
                </label>
                {!otpSent && (
                  <button data-testid="reg-send-otp-btn" onClick={sendOtp} disabled={loading}
                    className="w-full px-6 py-3.5 rounded-xl text-white font-semibold disabled:opacity-60 shadow-lg shadow-[#D71921]/25"
                    style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
                    {loading ? "Sending..." : "Send OTP"}
                  </button>
                )}
                {otpSent && (
                  <>
                    {devOtp && <div className="text-xs bg-gradient-to-r from-[#FCEDED] to-[#FFF5DA] border border-[#F2C97A] text-[#7A1010] rounded-lg p-3 font-mono">🛠 Dev OTP: <b>{devOtp}</b></div>}
                    <label className="block">
                      <span className="text-sm font-medium">Enter OTP</span>
                      <input data-testid="reg-otp-input" type="text" maxLength={6}
                        className="mt-2 w-full rounded-xl border border-[#E8E0D2] px-3 py-3 outline-none focus:border-[#D71921] focus:ring-2 focus:ring-[#D71921]/15 font-mono tracking-[0.4em]"
                        placeholder="000000" value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} />
                    </label>
                    <button data-testid="reg-verify-btn" onClick={verifyOtp} disabled={loading}
                      className="w-full px-6 py-3.5 rounded-xl text-white font-semibold disabled:opacity-60 shadow-lg shadow-[#D71921]/25"
                      style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
                      {loading ? "Verifying..." : "Verify OTP"}
                    </button>
                  </>
                )}
              </>
            )}
            {verified && (
              <>
                <div className="px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#E6F0E5] to-[#FFF5DA] border border-[#3F6B3F]/30 text-[#3F6B3F] text-xs flex items-center gap-2 font-semibold">
                  <BadgeCheck className="w-4 h-4" /> Phone {phone} verified
                </div>
                <label className="block">
                  <span className="text-sm font-medium">Full name</span>
                  <input data-testid="reg-name-input" type="text"
                    className="mt-2 w-full rounded-xl border border-[#E8E0D2] px-3 py-3 outline-none focus:border-[#D71921] focus:ring-2 focus:ring-[#D71921]/15"
                    placeholder="Ravi Kumar" value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Email <span className="text-xs text-[#6B5F58]">(optional)</span></span>
                  <input data-testid="reg-email-input" type="email"
                    className="mt-2 w-full rounded-xl border border-[#E8E0D2] px-3 py-3 outline-none focus:border-[#D71921] focus:ring-2 focus:ring-[#D71921]/15"
                    placeholder="ravi@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <label className="block">
                  <span className="text-sm font-medium flex items-center gap-1.5">
                    <Gift className="w-3.5 h-3.5 text-[#B8862A]" /> Referral code <span className="text-xs text-[#6B5F58]">(optional, ₹500 bonus)</span>
                  </span>
                  <input data-testid="reg-ref-input" type="text" maxLength={12}
                    className="mt-2 w-full rounded-xl border border-[#E8E0D2] px-3 py-3 outline-none focus:border-[#B8862A] focus:ring-2 focus:ring-[#B8862A]/15 uppercase font-mono"
                    placeholder="FRIEND123" value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())} />
                  {referralCode && (
                    <div className="mt-1 text-[11px] text-[#3F6B3F] font-medium">✓ You'll get ₹500 credit on your first lot!</div>
                  )}
                </label>
                <button data-testid="reg-submit-btn" onClick={finish} disabled={loading}
                  className="w-full px-6 py-3.5 rounded-xl text-white font-semibold disabled:opacity-60 shadow-lg shadow-[#D71921]/25"
                  style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 100%)" }}>
                  {loading ? "Creating..." : "Create account"}
                </button>
              </>
            )}
          </div>
          <div className="text-center mt-6 text-xs text-[#6B5F58]">
            Already a member? <Link to="/login" className="text-[#D71921] font-medium underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bullet({ icon: Icon, text }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-lg bg-white/15 grid place-items-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-[#F2C97A]" />
      </div>
      <span className="text-sm">{text}</span>
    </li>
  );
}
